// Preset filter types for Cross-Project and Combined views
// All data persisted in IndexedDB — no backend required

// ===== Time Range =====

export type CalendarPreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter';

export type TimeRangeMode =
  | { type: 'rolling'; hours: number; label: string }
  | { type: 'calendar'; preset: CalendarPreset }
  | { type: 'custom'; from: string; to: string };

// ===== Preset Definition =====

export interface PresetFilters {
  authorFilter?: string[];
  sourceFilter?: 'all' | 'git' | 'jira';
  hideMerges?: boolean;
  showLinkedOnly?: boolean;
}

export interface Preset {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  // Sources
  repoIds: string[];
  jiraProjectKey?: string;
  jiraBoardId?: number;
  confluenceSpaceKey?: string;
  // Time range
  timeRange: TimeRangeMode;
  // Display
  pinned: boolean;
  sortOrder: number;
  color?: string;
  // Saved filters
  filters?: PresetFilters;
}

// ===== Snapshot =====

export const ENGINE_VERSION = '1.0.0';

export type SnapshotStatus = 'ready' | 'stale' | 'failed';

export interface SnapshotMeta {
  id: string;
  presetId: string;
  resolvedFrom: string;
  resolvedTo: string;
  analysedAt: string;
  durationMs: number;
  compressedSize: number;
  uncompressedSize: number;
  status: SnapshotStatus;
  engineVersion: string;
  warnings?: string[];
  repoCount: number;
  commitCount: number;
  jiraEventCount: number;
  confluenceEventCount?: number;
  notionEventCount?: number;
  googleDocEventCount?: number;
}

export interface SnapshotBlob {
  id: string;
  presetId: string;
  compressedData: string;
}

// The raw data stored inside the compressed blob
export interface SnapshotData {
  gitCommits: any[];
  gitResults?: any[];
  aggregateStats?: any;
  jiraEvents: any[];
  jiraSprintSummary: any[];
  confluenceEvents?: any[];
  notionEvents?: any[];
  googleDocEvents?: any[];
  selectedJiraProject?: string;
  selectedConfluenceSpace?: string;
  selectedRepoNames?: string[];
  customFrom?: string;
  customTo?: string;
}

// ===== Hydrated (in-memory) =====

export interface HydratedSnapshot {
  meta: SnapshotMeta;
  data: SnapshotData;
  hydratedAt: number;
}

// ===== Analysis Job =====

export type AnalysisJobStatus = 'queued' | 'running' | 'done' | 'failed';

export interface AnalysisJob {
  presetId: string;
  status: AnalysisJobStatus;
  progress: number;
  message: string;
  startedAt?: number;
  error?: string;
}

// ===== Time Range Helpers =====

export function resolveTimeRange(tr: TimeRangeMode, tz?: string): { from: Date; to: Date } {
  const now = new Date();
  const timeZone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (tr.type === 'custom') {
    return { from: new Date(tr.from), to: new Date(tr.to) };
  }

  if (tr.type === 'rolling') {
    const to = now;
    const from = new Date(now.getTime() - tr.hours * 60 * 60 * 1000);
    return { from, to };
  }

  // Calendar presets — use user timezone for day boundaries
  const localNow = new Date(now.toLocaleString('en-US', { timeZone }));
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  switch (tr.preset) {
    case 'today':
      return { from: startOfDay(localNow), to: endOfDay(localNow) };
    case 'yesterday': {
      const y = new Date(localNow);
      y.setDate(y.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case 'this_week': {
      // Monday-based week
      const day = localNow.getDay();
      const diff = day === 0 ? 6 : day - 1; // Monday=0 offset
      const monday = new Date(localNow);
      monday.setDate(monday.getDate() - diff);
      return { from: startOfDay(monday), to: endOfDay(localNow) };
    }
    case 'last_week': {
      const day = localNow.getDay();
      const diff = day === 0 ? 6 : day - 1;
      const thisMonday = new Date(localNow);
      thisMonday.setDate(thisMonday.getDate() - diff);
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(lastMonday.getDate() - 7);
      const lastSunday = new Date(thisMonday);
      lastSunday.setDate(lastSunday.getDate() - 1);
      return { from: startOfDay(lastMonday), to: endOfDay(lastSunday) };
    }
    case 'this_month':
      return {
        from: startOfDay(new Date(localNow.getFullYear(), localNow.getMonth(), 1)),
        to: endOfDay(localNow),
      };
    case 'last_month': {
      const firstOfLastMonth = new Date(localNow.getFullYear(), localNow.getMonth() - 1, 1);
      const lastOfLastMonth = new Date(localNow.getFullYear(), localNow.getMonth(), 0);
      return { from: startOfDay(firstOfLastMonth), to: endOfDay(lastOfLastMonth) };
    }
    case 'this_quarter': {
      const q = Math.floor(localNow.getMonth() / 3);
      const firstOfQuarter = new Date(localNow.getFullYear(), q * 3, 1);
      return { from: startOfDay(firstOfQuarter), to: endOfDay(localNow) };
    }
    default:
      return { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), to: now };
  }
}

export function timeRangeLabel(tr: TimeRangeMode): string {
  if (tr.type === 'rolling') return tr.label;
  if (tr.type === 'custom') {
    const from = new Date(tr.from).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    const to = new Date(tr.to).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    return `${from} — ${to}`;
  }
  const labels: Record<CalendarPreset, string> = {
    today: 'Today',
    yesterday: 'All day yesterday',
    this_week: 'This week to date',
    last_week: 'Last week',
    this_month: 'This month to date',
    last_month: 'Last month',
    this_quarter: 'This quarter to date',
  };
  return labels[tr.preset] || tr.preset;
}

// Common rolling presets for the UI
export const ROLLING_PRESETS: TimeRangeMode[] = [
  { type: 'rolling', hours: 24, label: 'Last 24 hours' },
  { type: 'rolling', hours: 48, label: 'Last 48 hours' },
  { type: 'rolling', hours: 7 * 24, label: 'Last 7 days' },
  { type: 'rolling', hours: 14 * 24, label: 'Last 14 days' },
  { type: 'rolling', hours: 30 * 24, label: 'Last 30 days' },
  { type: 'rolling', hours: 90 * 24, label: 'Last 90 days' },
];

export const CALENDAR_PRESETS: TimeRangeMode[] = [
  { type: 'calendar', preset: 'today' },
  { type: 'calendar', preset: 'yesterday' },
  { type: 'calendar', preset: 'this_week' },
  { type: 'calendar', preset: 'last_week' },
  { type: 'calendar', preset: 'this_month' },
  { type: 'calendar', preset: 'last_month' },
  { type: 'calendar', preset: 'this_quarter' },
];
